import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { BookingFilters } from './components/BookingFilters';
import { BusinessBooking, BookingFilters as FilterTypes, BusinessActivity } from '@/types/businessAppTypes';
import { getBusinessBookings, getBusinessActivities } from '@/utils/businessAppApi';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

// Import the new DataTable component
import { DataTable, type TableColumn, type TableAction } from '../shared/DataTable';
import { Eye, Calendar, Users, DollarSign, CheckCircle, Clock, XCircle } from 'lucide-react';

export function BookingManagement() {
  const [bookings, setBookings] = useState<BusinessBooking[]>([]);
  const [activities, setActivities] = useState<BusinessActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterTypes>({});
  const { toast } = useToast();
  const { t } = useTranslation('business', { keyPrefix: 'bookings' });
  const { t: tc } = useTranslation('common');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // Fetch both bookings and activities
      const [bookingsData, activitiesData] = await Promise.all([
        getBusinessBookings(),
        getBusinessActivities()
      ]);
      
      console.log(`📊 [Bookings] Loaded ${bookingsData.length} bookings and ${activitiesData.length} activities`);
      
      setBookings(bookingsData);
      setActivities(activitiesData);
      setError(null);
    } catch (error) {
      console.error('❌ [Bookings] Error fetching initial data:', error);
      setError(tc('failedToLoad'));
      toast({
        title: tc('error'),
        description: tc('failedToLoad'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const data = await getBusinessBookings();
      // Apply filters here
      const filteredData = data.filter(booking => {
        const matchesDateRange = filters.dateRange
          ? new Date(booking.date) >= filters.dateRange.from && new Date(booking.date) <= filters.dateRange.to
          : true;

        const matchesActivity = filters.activity ? booking.activity === filters.activity : true;
        const matchesStatus = filters.status ? booking.status === filters.status : true;

        return matchesDateRange && matchesActivity && matchesStatus;
      });
      
      if (Object.keys(filters).length > 0) {
        console.log(`🔍 [Bookings] Applied filters: ${data.length} → ${filteredData.length} bookings`);
      }
      
      setBookings(filteredData);
      setError(null);
    } catch (error) {
      console.error('❌ [Bookings] Error fetching bookings:', error);
      setError(tc('failedToLoad'));
      toast({
        title: tc('error'),
        description: tc('failedToLoad'),
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Define table columns
  const columns: TableColumn<BusinessBooking>[] = [
    {
      key: 'bookingNumber',
      header: t('bookingId'),
      accessor: (item) => (
        <div className="font-mono text-sm font-medium">
          {item.bookingNumber}
        </div>
      ),
      sortable: true,
      width: '120px'
    },
    {
      key: 'customerName',
      header: t('customer'),
      accessor: (item) => (
        <div className="font-medium text-gray-900">
          {item.customerName || 'Unknown Customer'}
        </div>
      ),
      sortable: true
    },
    {
      key: 'activity',
      header: t('activity'),
      accessor: (item) => (
        <div className="font-medium text-gray-900">
          {item.activity}
        </div>
      ),
      sortable: true
    },
    {
      key: 'date',
      header: t('date'),
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <div className="text-sm">
            {format(new Date(item.date), 'MMM dd, yyyy')}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'participants',
      header: t('participants'),
      accessor: (item) => (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <div className="text-sm font-medium">
            {item.participants}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'status',
      header: t('status'),
      accessor: (item) => (
        <Badge className={getStatusColor(item.status)}>
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </Badge>
      ),
      sortable: true,
      width: '120px'
    },
    {
      key: 'paymentStatus',
      header: t('payment'),
      accessor: (item) => (
        <Badge className={getPaymentStatusColor(item.paymentStatus)}>
          {item.paymentStatus.charAt(0).toUpperCase() + item.paymentStatus.slice(1)}
        </Badge>
      ),
      sortable: true,
      width: '120px'
    },
    {
      key: 'total',
      header: t('total'),
      accessor: (item) => (
        <div className="text-right">
          <div className="font-bold text-gray-900">
            {item.total} {item.currency}
          </div>
        </div>
      ),
      sortable: true,
      className: 'text-right'
    }
  ];

  // Define table actions
  const actions: TableAction<BusinessBooking>[] = [
    {
      key: 'view',
      label: 'View Details',
      icon: Eye,
      onClick: (item) => {
        // TODO: Implement view details functionality
        console.log('View details for booking:', item.bookingNumber);
      }
    }
  ];

  return (
    <div className="space-y-6 p-8 min-h-screen">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('bookings')}</h1>
          <p className="text-gray-600 mt-1">
            Manage and track all your customer bookings
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{bookings.length}</div>
                <div className="text-sm text-gray-600">{t('totalBookings')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </div>
                <div className="text-sm text-gray-600">{t('confirmed')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'pending').length}
                </div>
                <div className="text-sm text-gray-600">{t('pending')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {bookings.filter(b => b.status === 'cancelled').length}
                </div>
                <div className="text-sm text-gray-600">{t('cancelled')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Legacy Filters (if still needed) */}
      {Object.keys(filters).length > 0 && (
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-4">
            <BookingFilters 
              onApplyFilters={setFilters} 
              activities={activities.map(activity => activity.name)} 
            />
          </CardContent>
        </Card>
      )}

      {/* Bookings Table */}
      <DataTable
        data={bookings}
        columns={columns}
        actions={actions}
        loading={loading}
        error={error}
        onRefresh={fetchInitialData}
        searchable={true}
        searchPlaceholder="Search by booking number, customer, or activity..."
        searchKeys={['bookingNumber', 'customerName', 'activity']}
        filterable={true}
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'pending', label: 'Pending' },
              { value: 'cancelled', label: 'Cancelled' }
            ]
          },
          {
            key: 'paymentStatus',
            label: 'Payment Status',
            options: [
              { value: 'paid', label: 'Paid' },
              { value: 'pending', label: 'Pending' },
              { value: 'failed', label: 'Failed' }
            ]
          }
        ]}
        emptyMessage={
          Object.keys(filters).length > 0 
            ? "Try adjusting your filters to see more results" 
            : "Your bookings will appear here once customers make reservations"
        }
        emptyIcon={Calendar}
      />
    </div>
  );
}